import AdDetailModel, { AdDetailsAttributeModel } from "@/models/AdDetailModel";
import { Badge, Box, Tooltip, Typography } from "@mui/material";
import HeightIcon from "@mui/icons-material/Height";
import PersonIcon from "@mui/icons-material/Person";
import ChecklistIcon from "@mui/icons-material/Checklist";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import ColorLensOutlinedIcon from "@mui/icons-material/ColorLensOutlined";
import GradingOutlinedIcon from "@mui/icons-material/GradingOutlined";
import CategoryIcon from "@mui/icons-material/Category";
import FemaleIcon from "@mui/icons-material/Female";
import MaleIcon from "@mui/icons-material/Male";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import Link from "next/link";
import { Fragment, JSX } from "react";
import { NAV_SELECTED_BACKGROUND_COLOR } from "@/app/layout/LayoutConstants";
import CheckIcon from "@mui/icons-material/Check";
import { JockeyIcon } from "@/app/shared/icons/JockeyIcon";

interface Slot {
  key: string;
  label: string | JSX.Element;
  icon: JSX.Element;
  spanMobile?: boolean;
  getValue: (ad: AdDetailModel) => AdDetailsAttributeModel | undefined | null;
  isNew?: boolean;
}

function CreateAdExtraSlot(label: string) {
  return {
    key: label,
    label: "",
    icon: <CheckIcon />,
    spanMobile: false,
    getValue: (ad: AdDetailModel): AdDetailsAttributeModel | null => {
      const adExtra = ad.adExtra?.find(
        (extra) => extra.label.toLowerCase() === label.toLowerCase(),
      );

      if (adExtra?.checkbox) {
        return {
          label: label,
          url: "",
        };
      }

      return null;
    },
  };
}

const HorseAdSummarySlots: Slot[] = [
  {
    key: "height",
    label: (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
        component="span"
      >
        Height{" "}
        <Tooltip title="Mature height is estimated for young horses">
          <InfoOutlinedIcon sx={{ fontSize: "16px" }} />
        </Tooltip>
      </Box>
    ),
    icon: <HeightIcon />,
    spanMobile: false,
    getValue: (ad: AdDetailModel) => ad.height,
  },
  {
    key: "breed",
    label: "Breed",
    icon: <WaterDropOutlinedIcon />,
    spanMobile: false,
    getValue: (ad: AdDetailModel) => ad.breed,
  },
  {
    key: "age",
    label: "Age",
    icon: <HourglassEmptyOutlinedIcon />,
    spanMobile: false,
    getValue: (ad: AdDetailModel) => ad.age,
  },
  {
    key: "colour",
    label: "Colour",
    icon: <ColorLensOutlinedIcon />,
    spanMobile: false,
    getValue: (ad: AdDetailModel) => ad.colour,
  },
  {
    key: "riderLevel",
    label: "Rider Level",
    icon: <PersonIcon />,
    spanMobile: false,
    getValue: (ad: AdDetailModel) => ad.riderLevel,
  },
  {
    key: "genderFemale",
    label: "Gender",
    icon: <FemaleIcon />,
    spanMobile: false,
    getValue: (ad: AdDetailModel) =>
      ad.gender && ["filly", "mare"].includes(ad.gender?.label?.toLowerCase())
        ? ad.gender
        : null,
  },
  {
    key: "genderMale",
    label: "Gender",
    icon: <MaleIcon />,
    spanMobile: false,
    getValue: (ad: AdDetailModel) =>
      ad.gender &&
      ["colt", "gelding", "stallion"].includes(ad.gender?.label?.toLowerCase())
        ? ad.gender
        : null,
  },
  {
    key: "brokenIn",
    label: "Training",
    icon: <SchoolOutlinedIcon />,
    spanMobile: false,
    getValue: (ad: AdDetailModel) => ad.brokenIn,
  },
  CreateAdExtraSlot("Live Cover"),
  CreateAdExtraSlot("Live Foal Guarantee"),
  CreateAdExtraSlot("Chilled Semen Available"),
  CreateAdExtraSlot("Frozen Semen Available"),
  {
    key: "offTheTrack",
    label: "",
    icon: <JockeyIcon size="medium" />,
    getValue: (ad: AdDetailModel) => ad.ott,
    isNew: true,
  },
  {
    key: "disciplines",
    label: "Disciplines",
    icon: <ChecklistIcon />,
    spanMobile: true,
    getValue: (ad: AdDetailModel) => ad.disciplines,
  },
  {
    key: "location",
    label: "Location",
    icon: <PlaceOutlinedIcon />,
    spanMobile: true,
    getValue: (ad: AdDetailModel) => ad.location,
  },
  {
    key: "registrations",
    label: "Registrations",
    icon: <GradingOutlinedIcon />,
    spanMobile: true,
    getValue: (ad: AdDetailModel) => ad.registrations,
  },
];

const NonHorseAdSummarySlots: Slot[] = [
  {
    key: "category",
    label: "Category",
    icon: <CategoryIcon />,
    spanMobile: false,
    getValue: (ad: AdDetailModel) => ad.category,
  },
  {
    key: "location",
    label: "Location",
    icon: <PlaceOutlinedIcon />,
    spanMobile: true,
    getValue: (ad: AdDetailModel) => ad.location,
  },
];

export default function AdSummary({ ad }: { ad: AdDetailModel }) {
  return (
    <Box
      sx={(theme) => ({
        display: "grid",
        gridTemplateColumns: `repeat(${ad.insurance ? "3" : "4"}, 1fr)`,
        columnGap: 4,
        rowGap: 2,
        ".span-mobile": {
          gridColumn: "span 2",
        },
        [theme.breakpoints.down("md")]: {
          gridTemplateColumns: "repeat(2, 1fr)",
          columnGap: 2,
          overflow: "hidden",
          ".span-mobile": {
            gridColumn: "span 2",
          },
        },
      })}
    >
      {(ad.isHorse ? HorseAdSummarySlots : NonHorseAdSummarySlots).map(
        (slot) => {
          const value = slot.getValue(ad);
          return (
            <Fragment key={slot.key}>
              {value?.label && (
                <Link
                  href={value.url}
                  className={slot.spanMobile ? "span-mobile" : ""}
                >
                  <Badge
                    color="primary"
                    badgeContent={slot.isNew ? "NEW!" : null}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: NAV_SELECTED_BACKGROUND_COLOR,
                          padding: 1,
                          borderRadius: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {slot.icon}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          "&&& > *": {
                            lineHeight: "1",
                          },
                        }}
                      >
                        <Typography
                          sx={(theme) => ({
                            fontWeight: "bold",
                            whiteSpace: "wrap",
                            [theme.breakpoints.down("sm")]: {
                              maxWidth: "260px",
                              overflow: "hidden",
                            },
                          })}
                        >
                          {value.label}
                        </Typography>
                        <Typography variant="caption">{slot.label}</Typography>
                      </Box>
                    </Box>
                  </Badge>
                </Link>
              )}
            </Fragment>
          );
        },
      )}
    </Box>
  );
}
